import React, { useEffect, useRef } from "react";
import { styled, css } from "styled-components";
import mediaQuery from "styles/mediaQuery";
import typographySizes from "styles/typographySizes";
import { Link } from "gatsby";

import { gsap } from "gsap";

//utils
import { getContrastColor } from "utils/colorUtils.js";

const buttonStyles = css`
height: 3.2rem;
padding: 0 1rem;
box-sizing: border-box;
font-size:${typographySizes.text.small.size.desktop}rem;
line-height:${typographySizes.text.small.lineHeight};
text-transform: uppercase;
background-color: ${({ color }) => color};

position: relative;
overflow: hidden;
cursor: pointer;

span{
color: ${({ color }) => getContrastColor(color)};
}

.btn-text-container {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

.btn-text-visible,
.btn-text-hidden {
  color: ${({ color }) => getContrastColor(color)};
  line-height: 3.2rem;
  display: inline-block;
  
  span {
    display: inline-block;
    will-change: transform;
  }
}

.btn-text-hidden {
  pointer-events: none;
  position: absolute;
  bottom: 100%;
  left: 0;
}

&.hovered .btn-text-visible span,
&.hovered .btn-text-hidden span {
  pointer-events: none;
}

align-self: flex-start;
`;

const Btn = styled.button`
${buttonStyles}
`;

const StyledLink = styled(Link)`
${buttonStyles}

`;

const CloseBtn = ({ onClick, children, to, color, className }) => {
  const btnRef = useRef(null);

  useEffect(() => {
    const btnElement = btnRef.current;
    if (!btnElement) return;

    // Function to wrap letters in spans
    const wrapLettersInSpan = (element) => {
      if (!element) return;
      const text = element.textContent;
      element.innerHTML = text
        .split("")
        .map((char) =>
          char === " "
            ? "<span>&nbsp;</span>"
            : `<span class="letter">${char}</span>`
        )
        .join("");
    };

    // Function to get child index
    const getChildIndex = (child) => {
      return Array.from(child.parentNode.children).indexOf(child);
    };

    // Wrap letters
    const visibleText = btnElement.querySelector(".btn-text-visible");
    const hiddenText = btnElement.querySelector(".btn-text-hidden");

    wrapLettersInSpan(visibleText);
    wrapLettersInSpan(hiddenText);

    // Add hover event listener
    const handleMouseOver = (e) => {
      if (
        !gsap.isTweening(
          btnElement.querySelectorAll(".btn-text-visible span")
        ) &&
        btnElement.classList.contains("hovered")
      ) {
        btnElement.classList.remove("hovered");
      }

      if (e.target.classList.contains("letter")) {
        btnElement.classList.add("hovered");
        const indexHover = getChildIndex(e.target);

        gsap.to(btnElement.querySelectorAll(".btn-text-visible span"), {
          yPercent: 100,
          ease: "back.out(2)",
          duration: 0.6,
          stagger: {
            each: 0.023,
            from: indexHover,
          },
        });

        gsap.to(btnElement.querySelectorAll(".btn-text-hidden span"), {
          yPercent: 100,
          ease: "back.out(2)",
          duration: 0.6,
          stagger: {
            each: 0.023,
            from: indexHover,
          },
          onComplete: () => {
            gsap.set(btnElement.querySelectorAll(".btn-text-visible span"), {
              clearProps: "all",
            });
            gsap.set(btnElement.querySelectorAll(".btn-text-hidden span"), {
              clearProps: "all",
            });
          },
        });
      }
    };

    btnElement.addEventListener("mouseover", handleMouseOver);

    return () => {
      btnElement.removeEventListener("mouseover", handleMouseOver);
    };
  }, [children]);

  if (to) {
    return (
      <StyledLink ref={btnRef} color={color} to={to} className="btn-link">
        <div className="btn-text-container">
          <span className="btn-text-hidden">{children}</span>
          <span className="btn-text-visible">{children}</span>
        </div>
      </StyledLink>
    );
  }

  return (
    <Btn
      ref={btnRef}
      color={color}
      onClick={onClick}
      className="btn-link"
      className={className}
    >
      <div className="btn-text-container">
        <span className="btn-text-hidden">{children}</span>
        <span className="btn-text-visible">{children}</span>
      </div>
    </Btn>
  );
};

export default CloseBtn;
